import { useCallback } from "react";
import {
  TextInput as RNTextInput,
  TextInputProps as RNTextInputProps,
} from "react-native";
import { colors, createStyles } from "../theme";

export type TextInputPublicProps<N extends string> = {
  disabled?: boolean;
  name: N;
  value: string | null;
  onChange: (name: N, value: string | null) => void;
  placeholder?: string;
  readOnly?: boolean;
};

type Props<N extends string> = Pick<
  RNTextInputProps,
  | "autoCapitalize"
  | "autoCorrect"
  | "autoFocus"
  | "inputMode"
  | "multiline"
  | "numberOfLines"
  | "onSubmitEditing"
  | "returnKeyType"
> &
  TextInputPublicProps<N>;

export function TextInput<N extends string>({
  disabled = false,
  name,
  onChange,
  readOnly = false,
  value,
  ...rest
}: Props<N>) {
  // Handlers
  const handleChange = useCallback(
    (newValue: string) => {
      onChange(name, !newValue ? null : newValue);
    },
    [onChange, name]
  );

  // Render
  return (
    <RNTextInput
      editable={!disabled && !readOnly}
      {...rest}
      value={value !== null ? `${value}` : ""}
      onChangeText={handleChange}
      placeholderTextColor={colors.onSurfaceDim}
      style={[
        textInputStyles.container,
        textInputStyles.text,
        rest.multiline && textInputStyles.multiline,
        disabled && textInputStyles.disabled,
      ]}
    />
  );
}

export const textInputStyles = createStyles({
  container: {
    height: 54,
    px: "6",
    backgroundColor: "surface",
    borderRadius: "9999",
  },
  multiline: {
    height: "auto",
    borderRadius: "8",
    py: "4",
  },
  text: {
    fontSize: 16,
    color: "onSurface",
  },
  disabled: {
    opacity: 0.5,
  },
});
